import type { TourBasicInfoState } from '@/shared/api/types/tour-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MIN_TOUR_DURATION_MINUTES, TICKET_LINK_PATTERN } from '@/shared/constants/tour-form'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { Switch } from '@/shared/ui/switch'

interface TourGeneralFieldsProps {
  value: TourBasicInfoState
  onChange: (value: TourBasicInfoState) => void
}

/** Duration, optional ticket link and the "featured" flag of a tour. */
export function TourGeneralFields({ value, onChange }: TourGeneralFieldsProps) {
  const { t } = useTranslation()
  const [ticketLinkTouched, setTicketLinkTouched] = useState(false)

  const ticketLinkError =
    ticketLinkTouched && value.ticketLink && !TICKET_LINK_PATTERN.test(value.ticketLink)
      ? t('tour.ticketLinkInvalid', {
          defaultValue: 'Link không hợp lệ. Phải bắt đầu bằng http:// hoặc https://',
        })
      : undefined

  return (
    <div className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField
          label={t('tour.duration')}
          required
          hint={t('tour.durationHint', {
            defaultValue: 'Tính bằng phút, tối thiểu {{min}} phút.',
            min: MIN_TOUR_DURATION_MINUTES,
          })}
        >
          <Input
            type="number"
            min={MIN_TOUR_DURATION_MINUTES}
            max={1440}
            value={value.duration}
            onChange={(e) => onChange({ ...value, duration: Number(e.target.value) || 0 })}
          />
        </FormField>

        <FormField
          label={t('tour.ticketLink', { defaultValue: 'Link mua vé' })}
          optional
          optionalLabel={t('common.optional', { defaultValue: 'tùy chọn' })}
          error={ticketLinkError}
        >
          <Input
            type="url"
            placeholder="https://..."
            value={value.ticketLink}
            onChange={(e) => onChange({ ...value, ticketLink: e.target.value })}
            onBlur={() => setTicketLinkTouched(true)}
          />
        </FormField>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-zinc-100 pt-5">
        <div>
          <p className="text-sm font-medium text-zinc-800">
            {t('tour.isFavorite', { defaultValue: 'Tour nổi bật' })}
          </p>
          <p className="text-xs text-zinc-500">
            {t('tour.isFavoriteHint', {
              defaultValue: 'Hiển thị tour này trong danh sách nổi bật',
            })}
          </p>
        </div>
        <Switch
          checked={value.isFavorite}
          onCheckedChange={(checked) => onChange({ ...value, isFavorite: checked })}
        />
      </div>
    </div>
  )
}
